import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { faDownload, faPencil } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService, PurchaseOrderRecord } from "src/services/Firebase/data";
import { Content, ContentFrame, colors } from "src/styles";
import Button from "src/components/Button";
import { generateAndDownloadPurchaseOrderPdf } from "src/utils/pdfGenerator";

const normalizeToArray = <T extends Record<string, any>>(input: unknown): T[] => {
  if (Array.isArray(input)) {
    return input.filter(Boolean) as T[];
  }

  if (input && typeof input === "object") {
    return Object.entries(input as Record<string, any>).map(([id, value]) => ({
      ...(value || {}),
      id: String((value as any)?.id || id || ""),
    })) as T[];
  }

  return [];
};

type PurchaseOrderColumnFilters = {
  type: string;
  poNumber: string;
  poDate: string;
  status: string;
  client: string;
  location: string;
  vendor: string;
  total: string;
  linkedWonum: string;
};

const EMPTY_COLUMN_FILTERS: PurchaseOrderColumnFilters = {
  type: "",
  poNumber: "",
  poDate: "",
  status: "",
  client: "",
  location: "",
  vendor: "",
  total: "",
  linkedWonum: "",
};

const tableHeaders = [
  "Type",
  "PO Number",
  "PO Date",
  "Status",
  "Client",
  "Location",
  "Vendor",
  "Total",
  "Linked WO",
  "Actions",
];

const filterFields = [
  { key: "type", label: "Type", placeholder: "Filter type" },
  { key: "poNumber", label: "PO Number", placeholder: "Filter PO number" },
  { key: "poDate", label: "PO Date", placeholder: "Filter date" },
  { key: "status", label: "Status", placeholder: "Filter status" },
  { key: "client", label: "Client", placeholder: "Filter client" },
  { key: "location", label: "Location", placeholder: "Filter location" },
  { key: "vendor", label: "Vendor", placeholder: "Filter vendor" },
  { key: "total", label: "Total", placeholder: "Filter total" },
  { key: "linkedWonum", label: "Linked WO", placeholder: "Filter linked WO" },
];

const getPurchaseDocumentTypeLabel = (documentType: PurchaseOrderRecord["documentType"]) => {
  if (documentType === "blanket_purchase_order") return "BPO";
  if (documentType === "purchase_requisition") return "PR";
  return "PO";
};

const formatPurchaseDate = (poDate?: string) => {
  if (!poDate) return "-";
  return new Date(poDate).toLocaleDateString();
};

const Purchasing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: clientUserId, isManager } = useSelector(getUserData);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<PurchaseOrderRecord[]>([]);
  const [isExportingRows, setIsExportingRows] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<PurchaseOrderColumnFilters>(EMPTY_COLUMN_FILTERS);

  const loadRows = async () => {
    setIsLoading(true);
    try {
      const { data } = await FirebaseDataService.getPurchaseOrders(clientUserId, !!isManager);
      setRows(normalizeToArray<PurchaseOrderRecord>(data));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setPageNameHeader("Purchasing"));
    void loadRows();

    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const searchFields = {
        type: getPurchaseDocumentTypeLabel(row.documentType),
        poNumber: row.poNumber || "",
        poDate: row.poDate ? new Date(row.poDate).toLocaleDateString() : "",
        status: row.status || "draft",
        client: row.companyName || "",
        location: row.locationName || "",
        vendor: row.vendorName || "",
        total: Number(row.totalAmount || 0).toFixed(2),
        linkedWonum: row.linkedWonum || "",
      };

      return Object.entries(columnFilters).every(([key, value]) => {
        const query = value.trim().toLowerCase();
        if (!query) return true;
        return String(searchFields[key as keyof typeof searchFields] || "")
          .toLowerCase()
          .includes(query);
      });
    });
  }, [rows, columnFilters]);

  const createCsvContent = (tableRows: string[][]) => {
    const escapeCell = (cell: string) => `"${String(cell).replace(/"/g, '""')}"`;
    return tableRows.map((row) => row.map(escapeCell).join(",")).join("\n");
  };

  const downloadCsv = (csvContent: string, filename: string) => {
    const csvWithBom = `\uFEFF${csvContent}`;
    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onClearFilters = () => {
    setColumnFilters(EMPTY_COLUMN_FILTERS);
  };

  const onChangeFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onClickExportPurchaseOrders = async () => {
    setIsExportingRows(true);

    try {
      if (!filteredRows.length) {
        return;
      }

      const headers = [
        "Type",
        "PO Number",
        "PO Date",
        "Status",
        "Client",
        "Location",
        "Vendor",
        "Total",
        "Linked WO",
        "Source",
      ];

      const exportRows = filteredRows.map((row) => [
        getPurchaseDocumentTypeLabel(row.documentType),
        row.poNumber || "",
        row.poDate ? new Date(row.poDate).toLocaleDateString() : "",
        row.status || "draft",
        row.companyName || "",
        row.locationName || "",
        row.vendorName || "",
        Number(row.totalAmount || 0).toFixed(2),
        row.linkedWonum || "",
        row.source || "",
      ]);

      const csvContent = createCsvContent([headers, ...exportRows]);
      const exportDate = new Date().toISOString().split("T")[0];
      downloadCsv(csvContent, `purchase-orders-${exportDate}.csv`);
    } finally {
      setIsExportingRows(false);
    }
  };

  return (
    <Content>
      <ContentFrame>
        <PageHeader>
          <div>
            <PageTitle>Purchasing</PageTitle>
            <PageSubtitle>
              All generated purchase orders and requisitions across manual creation and work request flows.
            </PageSubtitle>
          </div>
          <Button
            text="Create Purchase Document"
            onClick={() => {
              navigate("/purchasing/create");
            }}
          />
        </PageHeader>

        {isLoading ? (
          <LoadingState>
            <Spinner animation="border" size="sm" />
          </LoadingState>
        ) : !rows.length ? (
          <EmptyState>No purchase orders or requisitions found.</EmptyState>
        ) : (
          <Panel>
            <PanelHeader>
              <PanelTitle>All Purchasing Documents</PanelTitle>
              <PanelMeta>{`${filteredRows.length} shown`}</PanelMeta>
            </PanelHeader>

            <Toolbar>
              <Button
                text="Export"
                variant="small-secondary"
                onClick={onClickExportPurchaseOrders}
                disabled={isExportingRows || !filteredRows.length}
              />
              <Button text="Filter" variant="small-secondary" onClick={() => setShowColumnFilters((prev) => !prev)} />
              {showColumnFilters && <Button text="Clear" variant="text" onClick={onClearFilters} />}
            </Toolbar>

            {showColumnFilters && (
              <FiltersGrid>
                {filterFields.map((field) => (
                  <FilterField key={field.key}>
                    <FilterLabel>{field.label}</FilterLabel>
                    <FilterInput
                      value={columnFilters[field.key as keyof PurchaseOrderColumnFilters]}
                      placeholder={field.placeholder}
                      onChange={(e) => onChangeFilter(field.key, e.target.value)}
                    />
                  </FilterField>
                ))}
              </FiltersGrid>
            )}

            {filteredRows.length ? (
              <TableWrap>
                <TableHeader>
                  {tableHeaders.map((header) => (
                    <span key={header}>{header}</span>
                  ))}
                </TableHeader>
                {filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    <TypePill>{getPurchaseDocumentTypeLabel(row.documentType)}</TypePill>
                    <NameCell>{row.poNumber || "-"}</NameCell>
                    <MutedCell>{formatPurchaseDate(row.poDate)}</MutedCell>
                    <StatusCell>{row.status || "draft"}</StatusCell>
                    <MutedCell>{row.companyName || "-"}</MutedCell>
                    <MutedCell>{row.locationName || "-"}</MutedCell>
                    <MutedCell>{row.vendorName || "-"}</MutedCell>
                    <Right>{`$${Number(row.totalAmount || 0).toFixed(2)}`}</Right>
                    <MutedCell>{row.linkedWonum || "-"}</MutedCell>
                    <ActionsCell>
                      <ActionBtn
                        type="button"
                        aria-label="Edit purchase order"
                        title="Edit"
                        onClick={() => {
                          navigate(`/purchasing/${row.id}`);
                        }}
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </ActionBtn>
                      <ActionBtn
                        type="button"
                        aria-label="Download purchase order report"
                        title="Download report"
                        onClick={() => void generateAndDownloadPurchaseOrderPdf(row, `${row.poNumber || row.id}`)}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </ActionBtn>
                    </ActionsCell>
                  </TableRow>
                ))}
              </TableWrap>
            ) : (
              <EmptyRow>No purchase orders match these filters.</EmptyRow>
            )}
          </Panel>
        )}
      </ContentFrame>
    </Content>
  );
};

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  color: #1f2a37;
`;

const PageSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: #5d6977;
`;

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 0;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  padding: 12px 16px 16px;
  border-bottom: 1px solid ${colors.grey.light3};
  background: ${colors.grey.light6};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.grey.dark6};
`;

const FilterInput = styled.input`
  width: 100%;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  font-size: 12px;
  padding: 7px 8px;

  &:focus {
    outline: none;
    border-color: ${colors.grey.dark3};
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const TableGridBase = styled.div`
  display: grid;
  grid-template-columns: 90px 130px 110px 120px minmax(170px, 1fr) minmax(170px, 1fr) minmax(
      170px,
      1fr
    ) 100px 100px 96px;
  gap: 8px;
  align-items: center;
  min-width: 1460px;
`;

const TableHeader = styled(TableGridBase)`
  padding: 12px 16px;
  color: ${colors.grey.dark6};
  border-top: 1px solid ${colors.grey.light3};
  border-bottom: 1px solid ${colors.grey.light3};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TableRow = styled(TableGridBase)`
  padding: 14px 16px;
  border-top: 1px solid ${colors.grey.light3};
`;

const TypePill = styled.span`
  border: 1px solid ${colors.grey.light4};
  background: ${colors.grey.light6};
  font-size: 11px;
  font-weight: 700;
  color: ${colors.black.light1};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 8px;
  width: fit-content;
`;

const NameCell = styled.span`
  font-size: 13px;
  color: ${colors.black.dark1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MutedCell = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusCell = styled.span`
  font-size: 12px;
  color: ${colors.black.dark1};
  text-transform: capitalize;
`;

const Right = styled.span`
  text-align: right;
  font-size: 13px;
  color: ${colors.black.dark1};
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 6px;
`;

const EmptyRow = styled.div`
  padding: 20px 16px;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

const ActionBtn = styled.button`
  border: 1px solid ${colors.grey.dark3};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 13px;
  cursor: pointer;
  margin-right: 6px;

  &:last-child {
    margin-right: 0;
  }
`;

const LoadingState = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 26px;
  font-size: 13px;
  color: ${colors.grey.dark6};
`;

export default Purchasing;
